/**
 * 部标机设备报警列表的操作列
 */
import React, { useState } from 'react';
import { Button, Modal, message, Alert, Image } from 'antd';
import Thumbnail from '@/components/thumbnail';
import { jtDeviceAlarmVideoRequest, jtDeviceAlarmVideoUnfreezeRequest, OssEntity } from '@/service/jtDeviceAlarm';
import { getInnerPermissionEnable } from '@/utils/permission';
import { changeUrlProtocol } from '@/utils/common';
import { downloadFile } from '@/utils/download';
import styles from '@/styles/pageContent.module.scss';

interface JtDeviceAlarmOperateColumnProps {
  alarmData: any; // 报警信息
  downloadPermissionKey?: string; // 报警附件下载的权限key
}
const JtDeviceAlarmOperateColumn = (props: JtDeviceAlarmOperateColumnProps) => {
  const { alarmData, downloadPermissionKey } = props;
  const { contentMap, hasVideo, phone, ossKeyArr, osses } = alarmData;
  const alarmInfo = contentMap?.adas || contentMap?.blind || contentMap?.driver;
  const [attachmentInfo, setAttachmentInfo] = useState<any>({}); // 附件展示的信息
  const [unfreezeLoading, setUnfreezeLoading] = useState<boolean>(false); // 解冻loading状态
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false); // 下载loading状态

  // 下载文件
  const handleDownload = async (url: string, fileName: string) => {
    try {
      setDownloadLoading(true);
      await downloadFile(url, fileName);
    } catch (error) {
      message.error('下载失败');
    } finally {
      setDownloadLoading(false);
    }
  };

  // 解冻功能
  const onUnfreeze = async () => {
    if (!(osses || []).length) {
      message.warning('没有可解冻的附件');
      return;
    }

    setUnfreezeLoading(true);
    try {
      // 对所有附件进行解冻
      const unfreezeRequests = (osses || []).map((oss: OssEntity) => {
        const params: OssEntity = {
          ossKey: oss.ossKey,
          ossGroup: oss.ossGroup,
          ossBucket: oss.ossBucket
        };
        return jtDeviceAlarmVideoUnfreezeRequest(params);
      });

      const results = await Promise.allSettled(unfreezeRequests);

      // 检查是否所有请求都成功
      let hasError = false;
      let errorMsg = '';

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          hasError = true;
          errorMsg = result.reason?.message || '解冻请求失败';
        } else if (result.value?.data?.code !== 0) {
          hasError = true;
          errorMsg = result.value?.data?.msg || '解冻失败';
        }
      });

      if (hasError) {
        message.error(`解冻失败: ${errorMsg}`);
      } else {
        Modal.success({
          title: '解冻成功',
          content: '解冻请求已提交，从开始解冻到生效可能需要至多八个小时，请耐心等待。',
        });
      }
    } catch (error: any) {
      message.error(`解冻失败: ${error?.message || '未知错误'}`);
    } finally {
      setUnfreezeLoading(false);
    }
  };

  // 查看报警附件
  const onPlay = async () => {
    let fileName = '';
    if ((osses || []).length > 1) {
      // 有多个附件时显示缩略图
      const allAttachmentRequest = (osses || []).map((oss: { ossKey: string, ossGroup: string, ossBucket: string }) =>
         jtDeviceAlarmVideoRequest(oss.ossKey, oss.ossGroup)
      );
      const attachmentData: any = [];
      const res = await Promise.allSettled(allAttachmentRequest);
      res.forEach((item: any, index: number) => {
        attachmentData.push({
          url: changeUrlProtocol(item.value?.data?.data),
          contentType: osses[index].ossKey.endsWith('mp4') ? 'video' : 'image'
        });
      });
      setAttachmentInfo({ type: 'attachment', attachmentData });
    } else if ((osses || []).length === 1) {
      // 多种附件只有一条数据时直接显示
      const res = await jtDeviceAlarmVideoRequest(osses[0].ossKey, osses[0].ossGroup);
       setAttachmentInfo({
        type: osses[0]?.ossKey.endsWith('mp4') ? 'video' : 'image',
        url: changeUrlProtocol(res.data.data)
      });
    }
    //屏蔽下面分支，现在已经不会有下面的分支了
    // else if (hasVideo) {
    //   // 只支持视频附件的数据格式兼容
    //   let alarmKey = '';
    //   if (contentMap?.adas) {
    //     alarmKey = '64';
    //   } else if (contentMap?.blind) {
    //     alarmKey = '67';
    //   } else if (contentMap?.driver) {
    //     alarmKey = '65';
    //   }
    //   fileName = `02_${alarmKey}_${alarmKey}${alarmInfo.warningType
    //     .toString(16)
    //     .padStart(2, '0')}_${phone}_${formatTime(
    //       alarmInfo.time1,
    //       'YYYY-MM-DD HH-mm-ss'
    //     )}.mp4`;
    //   const res = await jtDeviceAlarmVideoRequest(fileName);
    //   setAttachmentInfo({
    //     type: 'video',
    //     url: changeUrlProtocol(res.data.data)
    //   });
    // }
  };

  // 兼容报警附件数据结构的变化
  if ((hasVideo && alarmInfo) || (osses || []).length) {
    const showDownload = getInnerPermissionEnable(downloadPermissionKey); // 是否可以下载
    const showUnfreeze = getInnerPermissionEnable('/car-admin/dvr-alarm/unfreeze'); // 是否可以解冻
    return (
      <div className={styles.tableOperateColumn}>
        <Button type="link" onClick={onPlay}>
          查看报警附件
        </Button>
        {showUnfreeze && (
          <Button type="link" onClick={onUnfreeze} loading={unfreezeLoading}>
            解冻
          </Button>
        )}
        {/* 预览视频的弹窗 */}
        {attachmentInfo?.type === 'video' && (
          <Modal
            title="视频播放"
            open
            footer={null}
            onCancel={() => setAttachmentInfo({})}
            centered
            maskClosable={false}
          >
            <Alert
              message='如果视频无法播放，可能是文件已冻结，请点击操作列中的"解冻"按钮进行解冻。'
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <video width="100%" height="240" controls src={attachmentInfo.url} id="playbackVideo">
              您的浏览器不支持 video 标签。
            </video>
            {showDownload && (
              <Button
                type="primary"
                onClick={() => handleDownload(attachmentInfo.url, '报警视频.mp4')}
                loading={downloadLoading}
                style={{ marginTop: 12 }}
              >
                下载
              </Button>
            )}
          </Modal>
        )}
        {/* 预览照片的弹窗 */}
        {attachmentInfo?.type === 'image' && (
          <Modal
            title="图片预览"
            open
            footer={null}
            onCancel={() => setAttachmentInfo({})}
            centered
          >
            <Alert
              message='如果图片无法显示，可能是文件已冻结，请点击操作列中的"解冻"按钮进行解冻。'
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Image src={attachmentInfo.url} alt="图片预览" width="100%" />
            {showDownload && (
              <Button
                type="primary"
                onClick={() => handleDownload(attachmentInfo.url, '报警图片.png')}
                loading={downloadLoading}
                style={{ marginTop: 12 }}
              >
                下载
              </Button>
            )}
          </Modal>
        )}
        {/* 预览多个附件的弹窗 */}
        {attachmentInfo?.type === 'attachment' && (
          <Modal
            title="报警附件"
            open
            footer={null}
            onCancel={() => setAttachmentInfo({})}
            centered
          >
            <Alert
              message='如果视频无法播放，可能是文件已冻结，请点击操作列中的"解冻"按钮进行解冻。'
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Thumbnail
              dataSource={attachmentInfo.attachmentData || []}
              downloadImgName="报警图片"
              downloadVideoName="报警视频"
              size="large"
              showDownload={showDownload}
            />
          </Modal>
        )}
      </div>
    );
  } else {
    return null;
  }
};

export default JtDeviceAlarmOperateColumn;
